import { useEffect } from 'react'

const PYLON_IFRAME_ID = 'pylon-chat-window'
const HIDE_STYLE_MARKER = 'data-counsel-hide-pylon'

/**
 * CSS that hides the "Powered by Pylon" sticky bar inside the chat iframe.
 *
 * The primary selector keys off the stable usepylon.com link href so it keeps
 * working even if Pylon changes its Tailwind class names; the second selector is
 * the explicit class-based fallback for the bar wrapper.
 */
const HIDE_STYLE_CSS = `
div.sticky.bottom-0:has(a[href*="usepylon.com"]),
div.bg-neutral-plus-1.sticky.bottom-0 {
  display: none !important;
}
`

/**
 * Injects the hide-branding stylesheet into a Pylon chat iframe's document.
 *
 * Using a standalone `<style>` node (rather than removing or inline-hiding the
 * node) means the rule lives outside Pylon's React tree, so it survives every
 * re-render and keeps the bar hidden no matter how often Pylon re-mounts it.
 *
 * @param iframe - The Pylon chat iframe element to inject into.
 * @returns `true` if the style is present after the call, otherwise `false`.
 */
const injectHideStyle = (iframe: HTMLIFrameElement): boolean => {
  try {
    const doc = iframe.contentDocument
    if (!doc) return false

    // Avoid duplicate injection if the style is already in place.
    if (doc.querySelector(`style[${HIDE_STYLE_MARKER}]`)) return true

    const styleEl = doc.createElement('style')
    styleEl.setAttribute(HIDE_STYLE_MARKER, 'true')
    styleEl.textContent = HIDE_STYLE_CSS

    const target = doc.head ?? doc.documentElement
    if (!target) return false

    target.appendChild(styleEl)
    return true
  } catch {
    // contentDocument access throws if the iframe is ever cross-origin; degrade
    // silently rather than breaking the app.
    return false
  }
}

/**
 * Continuously hides the "Powered by Pylon" branding bar in the Pylon support
 * chat iframe.
 *
 * Watches the parent document for the `#pylon-chat-window` iframe being added
 * (Pylon can destroy and recreate it), and injects a persistent stylesheet into
 * each iframe both immediately and on its `load` event.
 */
export const usePylonHidePoweredBy = (): void => {
  useEffect(() => {
    const loadHandlers = new WeakMap<HTMLIFrameElement, () => void>()

    const handleIframe = (iframe: HTMLIFrameElement) => {
      // Attempt injection now in case the document is already populated.
      injectHideStyle(iframe)

      if (loadHandlers.has(iframe)) return
      const onLoad = () => injectHideStyle(iframe)
      iframe.addEventListener('load', onLoad)
      loadHandlers.set(iframe, onLoad)
    }

    const scan = () => {
      const iframe = document.getElementById(PYLON_IFRAME_ID)
      if (iframe instanceof HTMLIFrameElement) handleIframe(iframe)
    }

    scan()

    const observer = new MutationObserver(scan)
    observer.observe(document.documentElement, { childList: true, subtree: true })

    return () => {
      observer.disconnect()
      const iframe = document.getElementById(PYLON_IFRAME_ID)
      if (iframe instanceof HTMLIFrameElement) {
        const onLoad = loadHandlers.get(iframe)
        if (onLoad) iframe.removeEventListener('load', onLoad)
      }
    }
  }, [])
}
